import { Inject, Injectable, Optional } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RollbarErrorHandlerService } from '@monsido/ng2/external/rollbar/rollbar-error-handler.service';
import { IS_MICROFRONTEND } from '@client/mfe/tokens/mfe-tokens';
import {
    shouldReport,
    buildReport,
    getThrottleKey,
    THROTTLE_WINDOW_MS,
    normalizeApiPath,
} from './http-failure-report.util';

interface ThrottleEntry {
    lastReported: number;
    suppressedCount: number;
}

/**
 * HTTP Interceptor that reports HTTP failures to Rollbar.
 *
 * Features:
 * - Reports status 0/-1 (CORS, network issues) as warnings
 * - Reports 5xx errors (server failures), with 502 as critical
 * - Skips 4xx errors (user-driven), aborts, and offline errors
 * - Dedupes reports per method/path/status within a time window
 * - Never reports successful requests (Rollbar telemetry covers those)
 */
@Injectable()
export class RollbarErrorInterceptor implements HttpInterceptor {
    private throttleMap = new Map<string, ThrottleEntry>();

    constructor (
        private rollbar: RollbarErrorHandlerService,
        @Optional() @Inject(IS_MICROFRONTEND) private readonly isMicrofrontend: boolean | null = null,
    ) { }

    intercept (req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const startTime = Date.now();

        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                this.handleError(error, req, startTime);
                return throwError(error);
            }),
        );
    }

    private handleError (
        error: HttpErrorResponse,
        request: HttpRequest<unknown>,
        startTime: number,
    ): void {
        // Check if we should report this error
        if (!shouldReport(error, request)) {
            return;
        }

        const durationMs = Date.now() - startTime;
        const normalizedPath = normalizeApiPath(request.url);
        const throttleKey = getThrottleKey(request.method, normalizedPath, error.status);

        // Check throttling
        const now = Date.now();
        const existingEntry = this.throttleMap.get(throttleKey);

        if (existingEntry && (now - existingEntry.lastReported) < THROTTLE_WINDOW_MS) {
            // Suppress this report, increment counter
            existingEntry.suppressedCount++;
            return;
        }

        // Calculate suppressed count for the report
        const suppressedCount = existingEntry ? existingEntry.suppressedCount : 0;

        // Update throttle map
        this.throttleMap.set(throttleKey, {
            lastReported: now,
            suppressedCount: 0,
        });

        // Build and send report
        const report = buildReport(error, request, durationMs, suppressedCount, this.isMicrofrontend === true);

        // Reporting must never change the error the caller receives. In local dev
        // RollbarErrorHandlerService.error()/critical() throw, so guard the call.
        try {
            switch (report.level) {
                case 'critical':
                    this.rollbar.critical(report.message, error, report.data);
                    break;
                case 'error':
                    this.rollbar.error(report.message, error, report.data);
                    break;
                case 'warning':
                    this.rollbar.warn(report.message, error, report.data);
                    break;
            }
        } catch {
            // ignore: logging failure must not mask the original HTTP error
        }
    }
}
